import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import { useLeague } from 'contexts/LeagueContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';
import DateFilter, { filterByDateRange } from 'components/ui/DateFilter';
import { formatGameRecap, shareRecap, textRecap } from 'utils/shareRecap';

const HistoryPage = () => {
  const navigate = useNavigate();
  const { games, gameStats, players, gamesLoading, deleteGame } = useApp();
  const { isAdmin } = useLeague();
  const [shareStatus, setShareStatus] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [dateRange, setDateRange] = useState('all');

  const filteredGames = useMemo(() => filterByDateRange(games, dateRange), [games, dateRange]);

  const enrichedGames = useMemo(() => {
    const playerMap = {};
    players?.forEach((p) => { playerMap[p?.id] = p; });

    const statsByGame = {};
    gameStats?.forEach((s) => {
      if (!statsByGame[s?.game_id]) statsByGame[s.game_id] = [];
      statsByGame[s.game_id].push(s);
    });

    return filteredGames?.map((game) => {
      const stats = statsByGame[game?.id] || [];
      const winners = stats?.filter((s) => s?.team === game?.winning_team)?.map((s) => ({
        ...s,
        name: s?.players?.name || playerMap[s?.player_id]?.name || 'Unknown',
      }));
      const losers = stats?.filter((s) => s?.team !== game?.winning_team)?.map((s) => ({
        ...s,
        name: s?.players?.name || playerMap[s?.player_id]?.name || 'Unknown',
      }));
      const winnersScore = winners?.reduce((sum, s) => sum + (s?.points || 0), 0);
      const losersScore = losers?.reduce((sum, s) => sum + (s?.points || 0), 0);

      return { ...game, winners, losers, winnersScore, losersScore };
    });
  }, [filteredGames, gameStats, players]);

  const handleShare = async (game) => {
    const text = formatGameRecap({
      date: game?.date, location: game?.location, winners: game?.winners,
      losers: game?.losers, winnersScore: game?.winnersScore, losersScore: game?.losersScore,
    });
    const result = await shareRecap(text, () => {
      setShareStatus((prev) => ({ ...prev, [game?.id]: 'copied' }));
    });
    if (result === 'shared' || result === 'copied') {
      setShareStatus((prev) => ({ ...prev, [game?.id]: result }));
      setTimeout(() => setShareStatus((prev) => ({ ...prev, [game?.id]: null })), 2500);
    } else if (result === 'error') {
      setShareStatus((prev) => ({ ...prev, [game?.id]: 'error' }));
      setTimeout(() => setShareStatus((prev) => ({ ...prev, [game?.id]: null })), 2500);
    }
  };

  const handleText = (game) => {
    const text = formatGameRecap({
      date: game?.date, location: game?.location, winners: game?.winners,
      losers: game?.losers, winnersScore: game?.winnersScore, losersScore: game?.losersScore,
    });
    textRecap(text);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteGame(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete game');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = dateStr?.includes('/') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
      return d?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr || '';
    }
  };

  const StatLine = ({ stat }) => (
    <div className="flex items-center justify-between py-1">
      <span className="font-body text-sm" style={{ color: 'var(--color-foreground)' }}>{stat?.name}</span>
      <span className="font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
        {stat?.points ?? 0} PTS &middot; {stat?.rebounds ?? 0} REB &middot; {stat?.assists ?? 0} AST
        {(stat?.steals > 0 || stat?.blocks > 0) && (
          <> &middot; {stat?.steals ?? 0} STL &middot; {stat?.blocks ?? 0} BLK</>
        )}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-4xl mx-auto">
          <div className="mb-6">
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>Game History</h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
              {enrichedGames?.length || 0} game{enrichedGames?.length !== 1 ? 's' : ''} {dateRange === 'all' ? 'recorded' : 'in selected range'}
            </p>
          </div>

          {/* Date range filter */}
          <div className="mb-4 overflow-x-auto pb-1">
            <DateFilter value={dateRange} onChange={setDateRange} />
          </div>

          {gamesLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
            </div>
          ) : enrichedGames?.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Calendar" size={40} color="var(--color-muted-foreground)" />
              <p className="font-caption text-sm mt-3" style={{ color: 'var(--color-muted-foreground)' }}>No games recorded yet.</p>
              <button
                onClick={() => navigate('/game-recording')}
                className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-lg font-caption text-sm font-medium"
                style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
              >
                <Icon name="ClipboardList" size={16} color="#fff" />
                Record a Game
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {enrichedGames?.map((game) => (
                <div
                  key={game?.id}
                  className="rounded-xl border overflow-hidden"
                  style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
                >
                  {/* Game header */}
                  <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2" style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-muted)' }}>
                    <div className="flex items-center gap-3">
                      <span className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>
                        {formatDate(game?.date)}
                      </span>
                      {game?.location && (
                        <span className="flex items-center gap-1 font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
                          <Icon name="MapPin" size={12} color="currentColor" />
                          {game?.location}
                        </span>
                      )}
                    </div>
                    <span className="font-data text-sm font-bold" style={{ color: 'var(--color-foreground)' }}>
                      <span style={{ color: 'var(--color-success, #16a34a)' }}>{game?.winnersScore}</span>
                      {' - '}
                      <span style={{ color: 'var(--color-error, #dc2626)' }}>{game?.losersScore}</span>
                    </span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg font-caption text-xs font-medium transition-all hover:opacity-80"
                        style={{ backgroundColor: 'rgba(232,93,4,0.1)', color: 'var(--color-primary)' }}
                        onClick={() => handleText(game)}
                        title="Send game recap via text message"
                      >
                        <Icon name="MessageSquare" size={14} color="currentColor" />
                        Text
                      </button>
                      <button
                        className="flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg font-caption text-xs font-medium transition-all hover:opacity-80"
                        style={{
                          backgroundColor: shareStatus[game?.id] === 'copied' || shareStatus[game?.id] === 'shared'
                            ? 'rgba(22,163,74,0.12)'
                            : shareStatus[game?.id] === 'error' ? 'rgba(220,38,38,0.08)' : 'rgba(232,93,4,0.1)',
                          color: shareStatus[game?.id] === 'copied' || shareStatus[game?.id] === 'shared'
                            ? '#16a34a' : shareStatus[game?.id] === 'error' ? '#dc2626' : 'var(--color-primary)',
                        }}
                        onClick={() => handleShare(game)}
                      >
                        <Icon
                          name={shareStatus[game?.id] === 'copied' || shareStatus[game?.id] === 'shared' ? 'Check' : shareStatus[game?.id] === 'error' ? 'AlertCircle' : 'Share2'}
                          size={14} color="currentColor"
                        />
                        {shareStatus[game?.id] === 'copied' ? 'Copied!' : shareStatus[game?.id] === 'shared' ? 'Shared!' : shareStatus[game?.id] === 'error' ? 'Failed' : 'Share'}
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            className="p-2.5 rounded-lg transition-all hover:opacity-80"
                            style={{ backgroundColor: 'rgba(232,93,4,0.1)' }}
                            onClick={() => navigate('/game-recording', { state: { editGame: game } })}
                            title="Edit game"
                          >
                            <Icon name="Pencil" size={14} color="var(--color-primary)" />
                          </button>
                          <button
                            className="p-2.5 rounded-lg transition-all hover:opacity-80"
                            style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}
                            onClick={() => { setDeleteError(null); setDeleteTarget(game); }}
                            title="Delete game"
                          >
                            <Icon name="Trash2" size={14} color="#DC2626" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Teams */}
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Icon name="Trophy" size={14} color="#16a34a" />
                        <span className="font-caption text-xs font-semibold uppercase tracking-wider" style={{ color: '#16a34a' }}>Winners</span>
                        <span className="font-data text-xs ml-auto" style={{ color: 'var(--color-muted-foreground)' }}>{game?.winnersScore} PTS</span>
                      </div>
                      {game?.winners?.map((s) => <StatLine key={s?.id || s?.player_id} stat={s} />)}
                    </div>
                    <div className="px-4 py-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Icon name="ThumbsDown" size={14} color="#dc2626" />
                        <span className="font-caption text-xs font-semibold uppercase tracking-wider" style={{ color: '#dc2626' }}>Losers</span>
                        <span className="font-data text-xs ml-auto" style={{ color: 'var(--color-muted-foreground)' }}>{game?.losersScore} PTS</span>
                      </div>
                      {game?.losers?.map((s) => <StatLine key={s?.id || s?.player_id} stat={s} />)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)', boxShadow: 'var(--shadow-lg)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950/30 flex items-center justify-center">
                <Icon name="Trash2" size={20} color="#DC2626" />
              </div>
              <h3 className="font-heading font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>Delete Game</h3>
            </div>

            <div className="space-y-1 mb-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-muted)' }}>
              <p className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{formatDate(deleteTarget.date)}</p>
              <p className="font-data text-sm" style={{ color: 'var(--color-foreground)' }}>
                <span style={{ color: '#16a34a' }}>{deleteTarget.winnersScore}</span> - <span style={{ color: '#dc2626' }}>{deleteTarget.losersScore}</span>
              </p>
            </div>

            <p className="font-caption text-sm mb-4" style={{ color: 'var(--color-muted-foreground)' }}>
              This will permanently delete this game and all player stats. This cannot be undone.
            </p>

            {deleteError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3 bg-red-50 dark:bg-red-950/20 border border-red-200">
                <Icon name="AlertCircle" size={14} color="#DC2626" />
                <span className="font-caption text-xs text-red-600">{deleteError}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="flex-1 h-10 rounded-lg border font-caption text-sm font-medium"
                style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 h-10 rounded-lg font-caption text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
